import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const TILE_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'
const TILE_REFERENCE_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}'
const TILE_ATTRIBUTION = '&copy; Esri, HERE, Garmin &amp; OpenStreetMap contributors'

const StaticMap = ({
  lat,
  lng,
  markers,
  zoom = 16,
  height = 220,
  markerColour = '#D24724',
  label = 'Building location map',
}) => {
  const containerRef = useRef(null)

  const points = useMemo(() => {
    const list = Array.isArray(markers) ? markers : [{ lat, lng }]
    return list
      .map(m => ({ lat: Number(m?.lat), lng: Number(m?.lng), colour: m?.colour || markerColour }))
      .filter(m => Number.isFinite(m.lat) && Number.isFinite(m.lng))
  }, [markers, lat, lng, markerColour])
  const pointsKey = points.map(p => `${p.lat},${p.lng},${p.colour}`).join('|')

  useEffect(() => {
    const node = containerRef.current
    if (!node || points.length === 0) return undefined

    const map = L.map(node, {
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      zoomControl: false,
      attributionControl: false,
      tap: false,
      preferCanvas: false,
    })

    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], zoom)
    } else {
      map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng])), { padding: [18, 18], maxZoom: Math.min(zoom, 15) })
    }

    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: 20,
    }).addTo(map)
    L.tileLayer(TILE_REFERENCE_URL, {
      maxZoom: 20,
    }).addTo(map)

    points.forEach(p => {
      L.circleMarker([p.lat, p.lng], {
        radius: points.length > 1 ? 7 : 9,
        weight: 3,
        color: '#ffffff',
        fillColor: p.colour,
        fillOpacity: 1,
      }).addTo(map)
    })

    map.invalidateSize()
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(node)
    return () => {
      ro.disconnect()
      map.remove()
    }
  }, [pointsKey, zoom])

  return (
    <MapBox
      ref={containerRef}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      role="img"
      aria-label={label}
    />
  )
}

export default StaticMap

const MapBox = styled.div`
  position: relative;
  isolation: isolate;
  z-index: 0;
  width: 100%;
  border-radius: inherit;
  background: #eef0f3;
  pointer-events: none;

  .leaflet-container {
    width: 100%;
    height: 100%;
    background: #eef0f3;
    font: inherit;
  }
`
